import { useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { SHEET, pieceLabel } from "../lib/pieces";

const SL = SHEET.length, SW = SHEET.width, T = SHEET.trim, K = SHEET.kerf;
const hue = (i) => `hsl(${(i * 47) % 360} 45% 32%)`;
const clamp = (a) => ({ ...a, x: Math.min(Math.max(a.x, T), SW - T - a.w), y: Math.min(Math.max(a.y, T), SL - T - a.h) });
const r1 = (v) => Math.round(v * 2) / 2;
const SNAP = 40;

// magnet: pull the dragged piece against the nearest neighbour edge (kerf gap kept) or the trim line
const pick = (pos, cands) => cands.reduce((o, [v, line]) => (Math.abs(v - pos) < o.d ? { v, line, d: Math.abs(v - pos) } : o), { v: pos, line: null, d: SNAP });
const snap = (a, list) => {
  const cx = [[T, T], [SW - T - a.w, SW - T]], cy = [[T, T], [SL - T - a.h, SL - T]];
  list.filter((b) => b.num !== a.num).forEach((b) => {
    if (a.y < b.y + b.h + SNAP && b.y < a.y + a.h + SNAP) cx.push([b.x + b.w + K, b.x + b.w + K], [b.x - K - a.w, b.x - K]);
    if (a.x < b.x + b.w + SNAP && b.x < a.x + a.w + SNAP) cy.push([b.y + b.h + K, b.y + b.h + K], [b.y - K - a.h, b.y - K]);
  });
  const x = pick(a.x, cx), y = pick(a.y, cy);
  return [{ ...a, x: x.v, y: y.v }, { x: x.line, y: y.line }];
};

export const clashes = (a, list) => list.some((b) => b.num !== a.num
  && a.x < b.x + b.w + K - 0.01 && b.x < a.x + a.w + K - 0.01 && a.y < b.y + b.h + K - 0.01 && b.y < a.y + a.h + K - 0.01);

const Toolbar = ({ sel, p, onMove, onRotate, sheetNo }) => {
  const [step, setStep] = useState(10);
  if (!sel) return <p className="text-xs text-[#6B7280]">Clique numa peça para selecionar. Arraste para mover — ela é puxada (ímã) para encostar na peça vizinha ou no refilo.</p>;
  const pos = (k, el) => { if (!onMove({ ...sel, [k]: Number(el.value) })) el.value = sel[k]; };
  return (
    <div data-testid={`sheet-editor-toolbar-${sheetNo}`} className="flex flex-wrap items-end gap-2 text-xs">
      <span className="font-mono font-bold text-[#E67E22] self-center">{pieceLabel(sel.num)} <span className="text-[#9CA3AF] font-normal">{p?.name}</span></span>
      {["x", "y"].map((k) => (
        <label key={k} className="space-y-0.5">
          <span className="block font-mono text-[10px] uppercase text-[#6B7280]">{k} (mm)</span>
          <Input data-testid={`sheet-editor-${k}-${sheetNo}`} key={`${sel.num}-${sel[k]}`} type="number" defaultValue={sel[k]}
            onBlur={(e) => pos(k, e.target)} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            className="h-8 w-24 bg-[#0F1115] border-[#2D333F] font-mono" />
        </label>
      ))}
      <label className="space-y-0.5">
        <span className="block font-mono text-[10px] uppercase text-[#6B7280]">passo</span>
        <Input data-testid={`sheet-editor-step-${sheetNo}`} type="number" value={step} onChange={(e) => setStep(Number(e.target.value) || 1)} className="h-8 w-16 bg-[#0F1115] border-[#2D333F] font-mono" />
      </label>
      {[[ArrowLeft, -1, 0, "left"], [ArrowRight, 1, 0, "right"], [ArrowUp, 0, 1, "up"], [ArrowDown, 0, -1, "down"]].map(([Icon, dx, dy, n]) => (
        <Button key={n} size="sm" variant="outline" data-testid={`sheet-editor-nudge-${n}-${sheetNo}`} onClick={() => onMove({ ...sel, x: sel.x + dx * step, y: sel.y + dy * step })}
          className="h-8 w-8 p-0 border-[#2D333F] bg-transparent"><Icon className="w-4 h-4" /></Button>
      ))}
      <Button size="sm" data-testid={`sheet-editor-rotate-${sheetNo}`} onClick={onRotate} className="h-8 bg-[#E67E22] hover:bg-[#D35400] text-black"><RotateCw className="w-4 h-4 mr-1" /> Girar</Button>
    </div>
  );
};

export const SheetEditor = ({ sheetNo, placed, pieces, onChange }) => {
  const svg = useRef(null);
  const [selNum, setSelNum] = useState(null);
  const [drag, setDrag] = useState(null);
  const [guide, setGuide] = useState({});
  const sel = placed.find((p) => p.num === selNum);

  const replace = (a) => onChange(placed.map((b) => (b.num === a.num ? a : b)));
  const commit = (a, msg) => {
    const c = clamp({ ...a, x: r1(a.x), y: r1(a.y) });
    if (clashes(c, placed)) { toast.error(msg || `Sem espaço: mantenha ${String(K).replace(".", ",")} mm entre as peças.`); return false; }
    replace(c);
    return true;
  };
  const rotate = () => {
    const p = pieces[sel.idx];
    if (p?.grain) { toast.error("Esta peça segue o veio do MDF e não pode ser girada."); return; }
    commit({ ...sel, w: sel.h, h: sel.w, rotated: !sel.rotated }, "Não há espaço para girar a peça nesta posição.");
  };

  const down = (pl, e) => {
    e.preventDefault();
    try { svg.current.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
    const inv = svg.current.getScreenCTM().inverse();
    setSelNum(pl.num);
    // move by the pointer delta (sheet units per pixel) so the toolbar appearing above can't shift the piece
    setDrag({ num: pl.num, orig: pl, k: inv.a, sx: e.clientX, sy: e.clientY });
  };
  const move = (e) => {
    if (!drag) return;
    const a = placed.find((p) => p.num === drag.num);
    const x = drag.orig.x + (e.clientX - drag.sx) * drag.k, y = drag.orig.y - (e.clientY - drag.sy) * drag.k;
    const [s, g] = snap(clamp({ ...a, x: r1(x), y: r1(y) }), placed);
    setGuide(g);
    replace(clamp(s));
  };
  const up = () => {
    if (!drag) return;
    setGuide({});
    const a = placed.find((p) => p.num === drag.num);
    if (clashes(a, placed)) {
      toast.error(`Posição inválida: mínimo de ${String(K).replace(".", ",")} mm entre as peças.`);
      replace(drag.orig);
    }
    setDrag(null);
  };

  return (
    <div className="space-y-2">
      <Toolbar sel={sel} p={sel && pieces[sel.idx]} sheetNo={sheetNo} onMove={(a) => commit(a)} onRotate={rotate} />
      <svg ref={svg} data-testid={`sheet-editor-svg-${sheetNo}`} viewBox={`-20 -20 ${SW + 40} ${SL + 40}`} className="w-full h-auto touch-none select-none"
        onPointerMove={move} onPointerUp={up} onPointerLeave={up}>
        <rect x={0} y={0} width={SW} height={SL} fill="#3A3226" stroke="#CA7C38" strokeWidth={6} />
        <rect x={T} y={T} width={SW - 2 * T} height={SL - 2 * T} fill="none" stroke="#6B7280" strokeWidth={3} strokeDasharray="20 12" />
        {placed.map((pl) => {
          const bad = clashes(pl, placed);
          const isSel = pl.num === selNum;
          return (
            <g key={pl.num} data-testid={`editor-piece-${pl.num}`} onPointerDown={(e) => down(pl, e)} className="cursor-move">
              <rect x={pl.x} y={SL - pl.y - pl.h} width={pl.w} height={pl.h} fill={bad ? "#7F1D1D" : hue(pl.idx)}
                stroke={bad ? "#F87171" : isSel ? "#FACC15" : "#F3F4F6"} strokeWidth={isSel ? 10 : 3} />
              {pl.w > 90 && pl.h > 50 && (
                <text x={pl.x + pl.w / 2} y={SL - pl.y - pl.h / 2} textAnchor="middle" dominantBaseline="middle" fill="#fff" pointerEvents="none"
                  fontFamily="JetBrains Mono" fontWeight="700" fontSize={Math.max(18, Math.min(60, Math.min(pl.w, pl.h) / 4))}>{pieceLabel(pl.num)}</text>
              )}
            </g>
          );
        })}
        {guide.x != null && <line data-testid={`snap-guide-x-${sheetNo}`} x1={guide.x} x2={guide.x} y1={0} y2={SL} stroke="#00E5FF" strokeWidth={4} strokeDasharray="18 10" pointerEvents="none" />}
        {guide.y != null && <line data-testid={`snap-guide-y-${sheetNo}`} x1={0} x2={SW} y1={SL - guide.y} y2={SL - guide.y} stroke="#00E5FF" strokeWidth={4} strokeDasharray="18 10" pointerEvents="none" />}
      </svg>
    </div>
  );
};
